import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './App.css';
import "bootstrap/dist/css/bootstrap.min.css";
import "./Assets/Styles/App.scss";
import AppRoutes from "./Routes/Routes";
import Navbar from "./Layout/Navbar";
import WelcomeNavbar from "./Layout/WelcomeNavbar";
import useTheme from "./hooks/useTheme";
import loading from "./Assets/Animations/loading.gif";

// Custom hook for robust intervals
function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

function App() {
  useTheme(); // Hook to apply the theme class to the body
  const { instance, accounts } = useMsal();
  const customerIsAuthenticated = accounts.length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(true);

  const lastActivityTimeRef = useRef(Date.now());
  const tokenRefreshIntervalRef = useRef(null);
  const [isUserActive, setIsUserActive] = useState(true);

  const handleUserActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    if (!isUserActive) {
      setIsUserActive(true);
      console.log("User now active");
    }
  }, [isUserActive]);

  const handleGetUserData = useCallback(async () => {
    if (!isUserActive || accounts.length === 0) {
      console.log("Token refresh skipped: User is inactive or no account is available.");
      return;
    }
    console.log("Attempting to refresh token...");
    try {
      const account = accounts[0];
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["https://ncmstagesynnexpartner.onmicrosoft.com/tasks-api/tasks.read"],
        account: account,
        forceRefresh: true,
      });
      console.log("Token successfully refreshed:", tokenResponse.accessToken);
      localStorage.setItem("access_token", tokenResponse.accessToken);
      lastActivityTimeRef.current = Date.now(); // Reset inactivity timer on successful refresh
    } catch (err) {
      console.error("Token refresh failed:", err);
    }
  }, [instance, isUserActive, accounts]);

  // Use the robust useInterval hook
  useInterval(
    () => {
      if (customerIsAuthenticated && isUserActive) {
        handleGetUserData();
      }
    },
    3 * 60 * 60 * 1000 // 3 hours
  );

  useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration in milliseconds
      once: true, // Whether animation should happen only once - while scrolling down
    });
    instance.handleRedirectPromise().finally(() => {
      setIsRedirecting(false);
    });
  }, [instance]);

  useEffect(() => {
      const checkUserActivity = () => {
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActivityTimeRef.current;
      const inactiveSeconds = Math.round(inactiveTime / 1000);
      console.log(
        `User active: ${isUserActive}. Inactive for: ${inactiveSeconds} seconds.`
      );

      // If inactive for more than 16 minutes, log out
      if (inactiveTime > 4 * 60 * 60 * 1000) {
        if (isUserActive) {
          setIsUserActive(false);
          console.log("User has become inactive. Logging out.");
          if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
            tokenRefreshIntervalRef.current = null;
          }
          localStorage.removeItem("navigation_link");
          localStorage.clear();
          navigate("/");
          instance.logoutRedirect();
        }
      }
    };

    const activityInterval = setInterval(checkUserActivity, 60 * 1000); // Check every 30 seconds

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      clearInterval(activityInterval);
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [handleUserActivity, instance, isUserActive, navigate]);

  useEffect(() => {
    if (customerIsAuthenticated && isUserActive) {
      handleGetUserData(); // Get token on initial load
    }
  }, [customerIsAuthenticated, isUserActive, handleGetUserData]);

  if (isRedirecting) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <img src={loading} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className="App">
      {location.pathname !== '/unauthorised-access' && (
        <>
          <AuthenticatedTemplate>
            <Navbar />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <WelcomeNavbar />
          </UnauthenticatedTemplate>
        </>
      )}
      <AppRoutes />
    </div>
  );
}

export default App;
